import React, { useEffect, useState } from 'react';
import { useNavigate, Link, NavLink, Navigate } from 'react-router-dom';
import { useT } from '../../i18n';
import { useAuth } from '../../context/AuthContext';
import useSeo from '../../hooks/useSeo';
import usePageChrome from '../../hooks/usePageChrome';
import './Landing.css';

// NOT lazy. It IS the first screen — code-splitting it would mean a hero that
// paints empty and fills in, which is the thing it exists to prevent.
import HeroBackdrop from './HeroBackdrop';
import LanguageModal from '../LanguageModal';
import LanguagePopover from '../LanguagePopover';
// The explanatory sections — steps, showcase, before/after, room types, the
// closing call to action. All presentational; see LandingSections.
import {
  LandingSteps, LandingFeatures, LandingTransform, LandingFinalCta,
} from './LandingSections';
// The two small previews of the ecosystem, each linking to the page that holds
// the rest of it.
import { LandingDesigners, LandingShops, LandingShop } from './LandingPreviews';

// RecommendationsFeed and LandingCatalogue are no longer imported here, and
// therefore no longer in this page's bundle. Both are left in the tree rather
// than deleted — the feed is the DASHBOARD's block and still used there, and
// the catalogue is a working component whose job (the whole marketplace, with
// filters) now belongs to /shop, which SearchPage already does properly. Same
// reasoning as RoomsCarousel above.

// The 3D room carousel that used to sit in the hero is gone. RoomsCarousel.jsx
// is left in place, unimported and therefore unbundled, rather than deleted —
// it is a working component and the R3F/GLB plumbing behind it is not trivial
// to reproduce, so it costs nothing to keep and something real to throw away.

// The primary navigation, once. Rendered as a row in the header on a desktop
// and as a list inside the language menu on a phone — see the two call sites.
//
// NO "MY PROJECTS" HERE. This header only ever renders for a signed-out
// visitor — a logged-in one is redirected to /dashboard before the page draws
// (see below) — so that link could only ever bounce off the auth guard and
// land on the sign-in screen. Sign In and Sign Up already sit to its right and
// say so plainly.
//
// AND NO "HOME" EITHER. Landing is mounted at `/` and nowhere else, so that
// entry was a link to the page it was being read on: permanently the active
// one, and doing nothing when pressed. Gone from the array rather than from the
// desktop row, so the gear menu on a phone loses it too — it was equally
// self-referential there.
const NAV = [
  { to: '/designers', k: 'nav_designers' },
  { to: '/shop', k: 'nav_marketplace' },
];

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

export default function Landing() {
  const navigate = useNavigate();
  const { t, langChosen } = useT();
  const { user } = useAuth();

  // The first-visit picker, and only that — the gear opens a dropdown of its
  // own (see LanguagePopover), so there is no second way in here.
  const [askLang, setAskLang] = useState(false);

  // ── FIRST VISIT ────────────────────────────────────────────────────────────
  // Nobody has chosen a language yet, so the site is showing its default and
  // the visitor has no way of knowing there are others. Asked once, on the
  // first screen; `langChosen` is set for good the moment they answer, so this
  // never fires again on any device where they have.
  useEffect(() => {
    if (!langChosen) setAskLang(true);
  }, [langChosen]);

  // ABOVE THE <Navigate> BELOW, because hooks cannot be conditional — moving
  // this under the early return would break the rules of hooks the moment a
  // signed-in user hits the page. Matches index.html's defaults on purpose:
  // this route IS the site root, and a scraper that never runs the bundle
  // should see the same thing a browser ends up with.
  useSeo({
    title: 'Tunify — AI Home Design & 3D Interior Planner',
    description: 'Design your home in 3D, plan rooms, add furniture, and generate realistic AI interior designs with Tunify.',
    path: '/',
  });

  // Matches the ink `.landing` fixes for itself, so iOS does not paint a pale
  // strip above the hero and below the footer. Same reasoning as AuthLayout.
  usePageChrome('#0B0D12');

  // A logged-in user opening the marketing site has nothing to do here —
  // send them straight to their own menu, same as Login/Register already do.
  // AuthProvider only renders its children once the initial auth check has
  // resolved, so `user` is already correct on this very first render — an
  // early-return <Navigate> (rather than a useEffect + imperative navigate)
  // means the landing page never flashes on screen first.
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="landing">
      {/* ── NAV ── */}
      <nav className="lnd-nav">
        <div className="lnd-logo">
          {/* alt="" because the word "Tunify" is the very next element. With a
              name on both, a screen reader announced the brand twice in a row.
              The mark is decorative WHEN the text is beside it; the footer's
              copy of this already had it right. */}
          <img src="/images/Tunify Original logo tab.png" alt="" className="lnd-logo-img" />
          <span className="lnd-logo-name">Tunify</span>
        </div>
        {/* ── WHERE ELSE THERE IS TO GO ────────────────────────────────────
            AI, the designers, the shop. DESKTOP ONLY: on a phone this row plus
            two auth buttons and the language gear is more chrome than the
            screen has width for. It is not simply dropped there — the NAV
            links are rendered inside the gear's menu below, and AI leads the
            shortcut row under the hero, so a phone reaches every one of them
            in one tap.

            ONE ARRAY, TWO PLACES. The desktop row and the phone menu are the
            same list; writing it twice is how a fourth destination ends up in
            one of them and not the other. */}
        <nav className="lnd-nav-links" aria-label={t('nav_primary')}>
          {/* ── AI, LEADING THE ROW ──────────────────────────────────────────
              DESKTOP ONLY, which is why it is rendered here rather than added
              to NAV: that array feeds the phone's gear menu as well, and the
              phone already has this destination as the leading card in the
              shortcut row under the hero. In NAV it would appear twice there.

              FIRST, and to /register rather than /try, for the same reason the
              shortcut card is: the links after it go to a directory, this one
              starts the thing the page is selling, and an account is what that
              needs. It used to sit second, behind Home; Home has gone (see NAV)
              and this inherits the position rather than trailing the
              directories.

              The animation is the shortcut card's, not a second one — see
              .lnd-nav-ai. Two AI buttons on one page pulsing to different
              clocks is the thing worth avoiding. */}
          <Link to="/register" className="lnd-nav-link lnd-nav-ai">
            <span className="lnd-nav-ai-ico" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
                strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 3.5 11.7 8 16.2 9.7 11.7 11.4 10 15.9 8.3 11.4 3.8 9.7 8.3 8Z" />
                <path d="M17.5 14.2 18.4 16.4 20.6 17.3 18.4 18.2 17.5 20.4 16.6 18.2 14.4 17.3 16.6 16.4Z" />
              </svg>
            </span>
            {t('nav_ai')}
          </Link>

          {NAV.map(n => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) => `lnd-nav-link${isActive ? ' lnd-nav-link--on' : ''}`}
            >{t(n.k)}</NavLink>
          ))}
        </nav>

        <div className="lnd-nav-right">
          {user ? (
            <button className="lnd-nav-avatar" onClick={() => navigate('/dashboard')} title={t('lnd_open_app')}>
              {getInitials(user.displayName || user.email?.split('@')[0] || 'Designer')}
            </button>
          ) : (
            <>
              <Link to="/login" className="lnd-nav-signin">{t('lnd_signin')}</Link>
              <button className="lnd-nav-open" onClick={() => navigate('/register')}>
                {t('lnd_signup')}
              </button>
            </>
          )}
          {/* LANGUAGE LIVES BEHIND THIS GEAR, after the sign-up button.
              It used to be three EN/RU/AM buttons sitting in the header at all
              times — permanent chrome for a thing chosen once and then never
              again, competing for width with the two actions the page exists
              for. Behind a control it costs one tap when wanted and nothing
              the rest of the time.

              A DROPDOWN, not the full-screen picker. That one is for a visitor
              who has never chosen; changing a language already set is a small
              correction and does not warrant blacking out the page.

              NO THEME TOGGLE beside it: this page sets its own palette and
              ignores the site theme (see the top of Landing.css), so that
              control changed nothing you could see here. */}
          {/* THE PHONE'S NAVIGATION LIVES IN HERE. The header row above is
              hidden below 900px, so on a phone this menu is where Designers
              and Shop are reached. The list is hidden again on a
              desktop, where the row has them — see .lgp-nav in Landing.css. */}
          <LanguagePopover triggerClassName="lnd-nav-gear">
            <div className="lgp-nav">
              <div className="lgp-label">{t('nav_primary')}</div>
              {NAV.map(n => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) => `lgp-nav-link${isActive ? ' lgp-nav-link--on' : ''}`}
                >{t(n.k)}</NavLink>
              ))}
            </div>
          </LanguagePopover>
        </div>
      </nav>

      {/* ── THE PAGE ITSELF ───────────────────────────────────────────────────
          A <main> around everything between the nav and the footer, so the
          document has the landmark a screen reader jumps to and a crawler
          reads as the primary content. PURELY SEMANTIC — <main> is display:
          block like the <div> layout this already relies on, .landing is a
          plain block (not flex or grid) so no child relationships change, and
          nothing in Landing.css selects `.landing > *`. Both were checked
          before this wrapper went in. */}
      <main className="lnd-main">

      {/* ── TWO SHORTCUTS, PHONES ONLY ────────────────────────────────────────
          Designers and the Shop, one tap from the top of the page.

          WHY IT EXISTS ONLY HERE. On a desktop these two are already in the
          header, a permanent row beside the logo. Below 900px that row is
          hidden and the same links move inside the hamburger menu — reachable,
          but two taps and behind a control you have to know to open. This puts
          them back on the surface for the width where they left it.

          ABOVE THE HERO, not below. It is a navigation aid, not a feature: a
          visitor who came to find a designer should not have to scroll past a
          full-height hero to learn the directory exists.

          NOT DUPLICATED IN THE MENU'S PLACE — the hamburger still lists them,
          because that is where someone looks for navigation once they have
          scrolled past this. */}
      <nav className="lnd-quick" aria-label={t('nav_primary')}>
        {/* FIRST, AND TO /register RATHER THAN /try. The other two go to a
            place; this one starts the thing the page is selling, and an
            account is what it needs. It leads because it is the action, not a
            directory. */}
        <Link to="/register" className="lnd-quick-card lnd-quick-card--ai">
          <span className="lnd-quick-ico" aria-hidden="true">
            {/* SPARKLES. The rayed circle that was here reads as a sun, or as
                a brightness control — the four-pointed star pair is the glyph
                that has come to mean "generated", and it is legible at 19px
                where a many-rayed sun turns to mush. */}
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
              strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 3.5 11.7 8 16.2 9.7 11.7 11.4 10 15.9 8.3 11.4 3.8 9.7 8.3 8Z" />
              <path d="M17.5 14.2 18.4 16.4 20.6 17.3 18.4 18.2 17.5 20.4 16.6 18.2 14.4 17.3 16.6 16.4Z" />
            </svg>
          </span>
          <span className="lnd-quick-label">{t('nav_ai')}</span>
          <span className="lnd-quick-arrow" aria-hidden="true">→</span>
        </Link>

        <Link to="/designers" className="lnd-quick-card">
          <span className="lnd-quick-ico" aria-hidden="true">
            {/* A PAINTBRUSH, not the key that was here. That glyph read as a
                wrench at 19px — maintenance, not design — which is the wrong
                promise for the interior-designer directory. A brush says the
                trade at a glance and survives being small. */}
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
              strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="m9.06 11.9 8.07-8.06a2.85 2.85 0 1 1 4.03 4.03l-8.06 8.08" />
              <path d="M7.07 14.94c-1.66 0-3 1.35-3 3.02 0 1.33-2.5 1.52-2 2.02 1.08 1.1 2.49 2.02 4 2.02 2.2 0 4-1.8 4-4.04a3.01 3.01 0 0 0-3-3.02z" />
            </svg>
          </span>
          <span className="lnd-quick-label">{t('nav_designers')}</span>
          <span className="lnd-quick-arrow" aria-hidden="true">→</span>
        </Link>

        <Link to="/shop" className="lnd-quick-card">
          <span className="lnd-quick-ico" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
              strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 8h14l-1.2 11.1a2 2 0 0 1-2 1.9H8.2a2 2 0 0 1-2-1.9L5 8z" />
              <path d="M9 8V6a3 3 0 0 1 6 0v2" />
            </svg>
          </span>
          <span className="lnd-quick-label">{t('nav_marketplace')}</span>
          <span className="lnd-quick-arrow" aria-hidden="true">→</span>
        </Link>
      </nav>

      {/* ── HERO ──
          FULL-BLEED, and therefore two elements rather than one: the section
          spans the viewport and carries the photographs, while the inner box
          keeps the 1100px measure the rest of the page is set to. Putting both
          jobs on one element meant either a centred column with a background
          that stopped at its edges, or a full-width background with copy
          running the whole way across. */}
      <section className="lnd-hero-band">
        <HeroBackdrop />
        <div className="lnd-hero">
        <div className="lnd-hero-text">
          <p className="lnd-hero-kicker">{t('lnd_kicker')}</p>
          <h1 className="lnd-hero-title">
            {t('lnd_title_pre')}<br />
            <span className="lnd-hero-title-hl">{t('lnd_title_hl')}</span><br />
            {t('lnd_title_post')}
          </h1>
          {/* TWO LENGTHS, ONE SHOWN. On a phone the full sentence ran to six
              lines and pushed the buttons toward the fold; the short one says
              the same three things in one. Both are in the markup and CSS
              picks — a `matchMedia` swap would re-render the hero on every
              resize and can disagree with the CSS breakpoint that lays the
              rest of it out. */}
          <p className="lnd-hero-sub lnd-hero-sub--long">{t('lnd_sub')}</p>
          <p className="lnd-hero-sub lnd-hero-sub--short">{t('lnd_sub_short')}</p>
          {/* BOTH BUTTONS OPEN THE BUILDER, and that is the change. The
              primary one used to go to /register — asking for an account
              before showing anything, on a page whose whole argument is "you
              can design a room right now". /try is the builder with no account
              at all, and signing up is one step further in (the guest save
              flow), not a gate in front. */}
          {/* ── WHICH ONE IS THE LOUD ONE ────────────────────────────────────
              Both are the same brass; only the WEIGHT is swapped. Sign up now
              carries the solid fill and "Try now" the outline — the two were
              the other way round.

              Nothing else changes: the colour was already `--lnd-gold` on both,
              so this is the `--ghost` modifier moving from one button to the
              other, not a repaint. The arrow stays with "Try now" because it
              means "straight into the builder", which is still true of the
              secondary action. */}
          <div className="lnd-cta-row">
            {/* SIGN UP FIRST. It is the solid one, and the solid button should
                also be the one the eye reaches first — in a left-to-right
                reading order that means leftmost. It was second, so the page's
                loudest control sat in the position people scan last. */}
            <button className="lnd-cta" onClick={() => navigate('/register')}>
              {t('lnd_signup')}
            </button>
            <button className="lnd-cta lnd-cta--ghost" onClick={() => navigate('/try')}>
              {t('lnd_cta')}
              <svg viewBox="0 0 16 16" fill="none" width="15" height="15">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        </div>
      </section>

      {/* ── WHAT THE PAGE SAYS, IN ORDER ──────────────────────────────────────
          The homepage used to go hero → recommendation rails → a filterable
          product catalogue with search, five filters and a pager. That order
          answered "what can I buy" before "what is this", and made a 3D design
          tool read as a furniture shop with a builder attached.

          It now argues the other way round: how it works, what the 3D and the
          AI do, what the result looks like — and only then the two things that
          surround the product. The designers and the shop are PREVIEWS with a
          link each; the full search, filters, sorting and paging live at
          /designers and /shop (see SearchPage). */}
      <LandingSteps />
      <LandingFeatures />
      <LandingTransform />
      {/* SIDE BY SIDE: the two directories are the same kind of thing — who is
          on Tunify — and stacking them made the page scroll through two nearly
          identical blocks before reaching the goods. Falls back to one column
          on narrow screens, and either section spans the full width on its own
          if the other has nothing to show. */}
      <div className="lsx-pair">
        <LandingDesigners />
        <LandingShops />
      </div>
      <LandingShop />
      <LandingFinalCta />

      </main>

      {/* ── FOOTER ── */}
      <footer className="lnd-footer">
        <span className="lnd-footer-brand">
          <img src="/images/Tunify Original logo tab.png" alt="" className="lnd-footer-logo" />
          Tunify
        </span>
        {/* The footer is where people look for these, and until now it held
            only a copyright line. Real <Link>s, so they stay inside the router
            rather than reloading the app. */}
        <nav className="lnd-footer-links" aria-label={t('nav_primary')}>
          <Link to="/about">{t('nav_about')}</Link>
          <Link to="/privacy">{t('nav_privacy')}</Link>
        </nav>
        <span className="lnd-footer-copy">{t('lnd_footer_copy')}</span>
      </footer>

      {/* The first-visit prompt cannot be dismissed without answering; the one
          opened from the gear can. Same dialog either way. */}
      {askLang && <LanguageModal dismissible={false} onClose={() => setAskLang(false)} />}
    </div>
  );
}
